import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { CloudOff, Loader2, X } from "lucide-react";
import MarketDetailAbout from "../components/bond-predict/detail/MarketDetailAbout";
import MarketDetailOrderBook from "../components/bond-predict/detail/MarketDetailOrderBook";
import MarketDetailOrderModal from "../components/bond-predict/detail/MarketDetailOrderModal";
import MarketDetailPriceChart from "../components/bond-predict/detail/MarketDetailPriceChart";
import MarketDetailRail from "../components/bond-predict/detail/MarketDetailRail";
import MarketDetailShell from "../components/bond-predict/detail/MarketDetailShell";
import { selectAppUser, checkAppAuth } from "../redux/appAuthSlice";
import {
  clearBondPredictCurrentMarket,
  fetchBondPredictMarketById,
  fetchBondPredictMarketTrades,
  fetchBondPredictOrderBook,
  selectBondPredictCurrentMarket,
  selectBondPredictCurrentMarketError,
  selectBondPredictCurrentMarketStatus,
  selectBondPredictMarketTrades,
  selectBondPredictMarketTradesStatus,
  selectBondPredictOrderBook,
  selectBondPredictOrderBookStatus,
} from "../redux/bondPredictSlice";
import {
  getDefaultSelectedOutcome,
  getSelectedOutcomeLabel,
  getTradeCtaLabel,
  isOutcomeTradable,
} from "../utils/bondPredictDetailHelpers";
import { formatVolume, getMarketTitle } from "../utils/bondPredictHelpers";
import { usePersistedPanelSize } from "../hooks/usePersistedPanelSize";
import "../bond-predict.css";
import "../bond-predict-detail-shell.css";
import "../bond-predict-detail.css";
import "../bond-predict-trade-modal.css";

const LIST_PATH = "/app/bond-predict";
const DETAIL_RAIL_DEFAULT = 312;
const DETAIL_RAIL_MIN = 68;
const DETAIL_RAIL_MAX = 420;
const DETAIL_RAIL_COMPACT = 120;

const BondPredictDetailFrame = ({ children, railSidebar, railCollapsed, railWidth }) => (
  <div
    className={`bpd-shell${railCollapsed ? " bpd-rail-collapsed" : ""}`}
    style={{ "--bpd-rail-w": `${railWidth}px` }}
  >
    <div className="bpd-ambient" aria-hidden>
      <span className="bpd-ambient-orb bpd-ambient-orb-a" />
      <span className="bpd-ambient-orb bpd-ambient-orb-b" />
    </div>
    <div className="bpd-shell-grid">
      {railSidebar}
      <div className="bpd-shell-main">{children}</div>
    </div>
  </div>
);

const BondPredictDetail = () => {
  const { marketId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectAppUser);
  const market = useSelector(selectBondPredictCurrentMarket);
  const status = useSelector(selectBondPredictCurrentMarketStatus);
  const error = useSelector(selectBondPredictCurrentMarketError);
  const orderBook = useSelector(selectBondPredictOrderBook);
  const orderBookStatus = useSelector(selectBondPredictOrderBookStatus);
  const marketTrades = useSelector(selectBondPredictMarketTrades);
  const marketTradesStatus = useSelector(selectBondPredictMarketTradesStatus);
  const [selectedOutcome, setSelectedOutcome] = useState(null);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [outcomeDrawerOpen, setOutcomeDrawerOpen] = useState(false);
  const {
    size: railWidth,
    setClampedSize: setRailWidth,
    reset: resetRailWidth,
    minSize: railMin,
    maxSize: railMax,
  } = usePersistedPanelSize("bb-bp-detail-rail-w", {
    defaultSize: DETAIL_RAIL_DEFAULT,
    minSize: DETAIL_RAIL_MIN,
    maxSize: DETAIL_RAIL_MAX,
  });
  const [isMobileRail, setIsMobileRail] = useState(false);
  const isRailCollapsed = railWidth <= DETAIL_RAIL_COMPACT;
  const railCollapsed = !isMobileRail && isRailCollapsed;

  const toggleRailWidth = useCallback(() => {
    if (isRailCollapsed) resetRailWidth();
    else setRailWidth(DETAIL_RAIL_MIN);
  }, [isRailCollapsed, resetRailWidth, setRailWidth]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 960px)");
    const syncRailForMobile = () => {
      const matches = mq.matches;
      setIsMobileRail(matches);
      if (!matches) setOutcomeDrawerOpen(false);
    };
    syncRailForMobile();
    mq.addEventListener("change", syncRailForMobile);
    return () => mq.removeEventListener("change", syncRailForMobile);
  }, []);

  const handleRailResizePointerDown = useCallback(
    (event) => {
      event.preventDefault();
      const startX = event.clientX;
      const startSize = railWidth;
      document.body.classList.add("ws-is-resizing");

      const onMove = (ev) => {
        setRailWidth(startSize + (ev.clientX - startX));
      };

      const onUp = () => {
        document.body.classList.remove("ws-is-resizing");
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    },
    [railWidth, setRailWidth]
  );

  const load = useCallback(async () => {
    if (!marketId) return;
    await dispatch(fetchBondPredictMarketById(marketId));
  }, [dispatch, marketId]);

  useEffect(() => {
    load();
    return () => dispatch(clearBondPredictCurrentMarket());
  }, [load, dispatch]);

  useEffect(() => {
    if (!market) return;
    setSelectedOutcome((prev) => {
      if (prev?._id && market.outcomes?.some((o) => o._id === prev._id)) return prev;
      return getDefaultSelectedOutcome(market);
    });
  }, [market]);

  useEffect(() => {
    if (!marketId || !selectedOutcome?._id) return;
    dispatch(
      fetchBondPredictOrderBook({ marketId, outcomeId: selectedOutcome._id })
    );
    dispatch(
      fetchBondPredictMarketTrades({ marketId, outcomeId: selectedOutcome._id })
    );
  }, [dispatch, marketId, selectedOutcome?._id]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(t);
  }, [toast]);

  const tradable = useMemo(
    () => isOutcomeTradable(market, selectedOutcome),
    [market, selectedOutcome]
  );

  const tradeCta = useMemo(
    () => getTradeCtaLabel(market, selectedOutcome, tradable),
    [market, selectedOutcome, tradable]
  );

  const shellMeta = useMemo(() => {
    if (!market) return [];
    const items = [];
    if (market.metrics?.totalVolume) {
      items.push(`${formatVolume(market.metrics.totalVolume)} vol`);
    }
    if (market.metrics?.totalTrades) {
      items.push(`${market.metrics.totalTrades} trades`);
    }
    return items;
  }, [market]);

  const handleOpenOrderModal = () => {
    if (!user?._id) {
      setToast({ tone: "warn", text: "Sign in to place orders." });
      return;
    }
    if (!selectedOutcome || !tradable) return;
    setOrderModalOpen(true);
  };

  const handleOrderPlaced = async (toastPayload) => {
    if (toastPayload) setToast(toastPayload);
    await load();
    if (selectedOutcome?._id) {
      dispatch(
        fetchBondPredictOrderBook({ marketId, outcomeId: selectedOutcome._id })
      );
      dispatch(
        fetchBondPredictMarketTrades({ marketId, outcomeId: selectedOutcome._id })
      );
    }
    dispatch(checkAppAuth());
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await load();
      if (marketId && selectedOutcome?._id) {
        dispatch(fetchBondPredictOrderBook({ marketId, outcomeId: selectedOutcome._id }));
        dispatch(fetchBondPredictMarketTrades({ marketId, outcomeId: selectedOutcome._id }));
      }
    } finally {
      setRefreshing(false);
    }
  };

  const handleSelectOutcome = (outcome) => {
    setSelectedOutcome(outcome);
    if (isMobileRail) setOutcomeDrawerOpen(false);
  };

  const loading = status === "loading" && !market;
  const selectedLabel = selectedOutcome
    ? getSelectedOutcomeLabel(selectedOutcome, market)
    : null;
  const detailRailSidebar = (
    <div className="bpd-rail-sidebar" aria-label="Market navigation">
      <MarketDetailRail
        market={market}
        onRefresh={handleRefresh}
        refreshing={refreshing}
        selectedOutcomeId={selectedOutcome?._id}
        onSelectOutcome={handleSelectOutcome}
        collapsed={railCollapsed}
        onToggleCollapse={toggleRailWidth}
      />
      <div
        className="bp-filter-rail-resize-handle"
        role="separator"
        aria-orientation="vertical"
        aria-valuenow={railWidth}
        aria-valuemin={railMin}
        aria-valuemax={railMax}
        aria-label="Resize market rail"
        title="Drag to resize · double-click to reset"
        onPointerDown={handleRailResizePointerDown}
        onDoubleClick={resetRailWidth}
      />
    </div>
  );

  if (loading) {
    return (
      <BondPredictDetailFrame
        railSidebar={detailRailSidebar}
        railCollapsed={railCollapsed}
        railWidth={railWidth}
      >
        <div className="bpd-scroll bpd-scroll-center">
          <div className="bpd-loading">
            <Loader2 size={18} className="bp-spin" />
            <span>Loading market</span>
          </div>
        </div>
      </BondPredictDetailFrame>
    );
  }

  if (!market || error) {
    return (
      <BondPredictDetailFrame
        railSidebar={detailRailSidebar}
        railCollapsed={railCollapsed}
        railWidth={railWidth}
      >
        <MarketDetailShell title="Unavailable" />
        <div className="bpd-scroll">
          <div className="bpd-page-inner">
            <div className="bpd-empty">
              <CloudOff size={22} />
              <p className="bpd-empty-title">{error || "Market not found"}</p>
              <button type="button" className="bpd-empty-btn" onClick={() => navigate(LIST_PATH)}>
                Back to markets
              </button>
            </div>
          </div>
        </div>
      </BondPredictDetailFrame>
    );
  }

  return (
    <BondPredictDetailFrame
      railSidebar={detailRailSidebar}
      railCollapsed={railCollapsed}
      railWidth={railWidth}
    >
      {toast ? (
        <div className={`bp-toast bp-toast-${toast.tone || "info"}`} role="status">
          {toast.text}
        </div>
      ) : null}

      <div className="bpd-stage">
        <div className="bpd-desk-top">
          <MarketDetailShell
            market={market}
            title={getMarketTitle(market)}
            meta={shellMeta}
            onOutcomeMenuToggle={isMobileRail ? () => setOutcomeDrawerOpen((v) => !v) : undefined}
            outcomeMenuOpen={outcomeDrawerOpen}
            selectedOutcomeLabel={selectedLabel}
          />
        </div>

        <div className="bpd-stage-body">
          <div className="bpd-stage-depth">
            {selectedLabel ? (
              <p className="bpd-selection-line">
                Selected <strong>{selectedLabel}</strong>
              </p>
            ) : null}
            <MarketDetailPriceChart
              market={market}
              selectedOutcome={selectedOutcome}
              trades={marketTrades}
              status={marketTradesStatus}
            />
            <MarketDetailOrderBook
              market={market}
              selectedOutcome={selectedOutcome}
              orderBook={orderBook}
              orderBookStatus={orderBookStatus}
              onRefresh={() => {
                if (selectedOutcome?._id) {
                  dispatch(
                    fetchBondPredictOrderBook({
                      marketId,
                      outcomeId: selectedOutcome._id,
                    })
                  );
                }
              }}
              onTrade={handleOpenOrderModal}
              tradeDisabled={!tradable || !selectedOutcome}
              tradeLabel={tradeCta}
            />
          </div>

          <aside
            className="bpd-stage-context"
            id="bpd-section-context"
            aria-label="Market context"
          >
            <MarketDetailAbout market={market} />
          </aside>
        </div>
      </div>

      <MarketDetailOrderModal
        open={orderModalOpen}
        onClose={() => setOrderModalOpen(false)}
        market={market}
        selectedOutcome={selectedOutcome}
        orderBook={orderBook}
        orderBookStatus={orderBookStatus}
        userCoin={user?.COIN}
        onPlaced={handleOrderPlaced}
      />

      {outcomeDrawerOpen ? (
        <div
          className="bpd-market-drawer-backdrop"
          onClick={() => setOutcomeDrawerOpen(false)}
          role="presentation"
        >
          <aside
            className="bpd-market-drawer"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-label="Choose market outcome"
          >
            <button
              type="button"
              className="bpd-market-drawer-close"
              onClick={() => setOutcomeDrawerOpen(false)}
              aria-label="Close outcome menu"
            >
              <X size={16} />
            </button>
            <MarketDetailRail
              market={market}
              onRefresh={handleRefresh}
              refreshing={refreshing}
              selectedOutcomeId={selectedOutcome?._id}
              onSelectOutcome={handleSelectOutcome}
              collapsed={false}
            />
          </aside>
        </div>
      ) : null}
    </BondPredictDetailFrame>
  );
};

export default BondPredictDetail;
